import { useCallback, useMemo, useRef, useState } from "react";

import { ArpSegment, ForecastConstant } from "models/UserArpsModel";

export enum ProductOperation {
  DELETE = "delete",
  ADD = "add"
}

export type ProductCommandData = {
  segments: ArpSegment[];
  constants: ForecastConstant[];
  template: string | null;
  calculatedFields: string[];
};

// Command object representing a field-level change
export interface FieldCommand {
  execute: () => void;
  undo: () => void;
  fieldPath: string;
  oldValue: number;
  newValue: number;
}

export function createFieldCommand(
  fieldPath: string,
  oldValue: number,
  newValue: number,
  execute: () => void,
  undo: () => void
): FieldCommand {
  return {
    fieldPath,
    oldValue,
    newValue,
    execute,
    undo
  };
}

// Command object representing a product-level operation (deletion, addition)
export interface ProductCommand {
  execute: () => void;
  undo: () => void;
  productName: string;
  operation: ProductOperation;
  productData: ProductCommandData;
}

export function createProductCommand(
  productName: string,
  operation: ProductOperation,
  productData: ProductCommandData,
  execute: () => void,
  undo: () => void
): ProductCommand {
  return {
    productName,
    operation,
    productData,
    execute,
    undo
  };
}

export interface IArpsUndoManager {
  executeFieldCommand: (command: FieldCommand) => void;
  executeProductCommand: (command: ProductCommand) => void;
  addCommand: (command: FieldCommand | ProductCommand) => void;
  undo: () => void;
  reset: () => void;
  removeCommandsForProduct: (product: string) => void;
  canUndo: boolean;
  getLastChangedField: () => string | null;
}

// Custom hook for ARPS undo/redo functionality
const useArpsUndoManager = (): IArpsUndoManager => {
  const [currentState, setCurrentState] = useState<number>(0);
  const currentStateRef = useRef<number>(0);

  const [commands, setCommands] = useState<(FieldCommand | ProductCommand)[]>([]);
  const commandsRef = useRef<(FieldCommand | ProductCommand)[]>([]);

  // Function to execute a field command
  const executeFieldCommand = useCallback(
    (command: FieldCommand) => {
      command.execute();

      // Store the command in history
      const newCommands = [...commands.slice(0, currentStateRef.current), command];
      setCommands(newCommands);
      commandsRef.current = newCommands;
      currentStateRef.current += 1;
      setCurrentState(currentStateRef.current);
    },
    [commands]
  );

  // Function to execute a product command
  const executeProductCommand = useCallback(
    (command: ProductCommand) => {
      command.execute();

      // Store the command in history
      const newCommands = [...commands.slice(0, currentStateRef.current), command];
      setCommands(newCommands);
      commandsRef.current = newCommands;
      currentStateRef.current += 1;
      setCurrentState(currentStateRef.current);
    },
    [commands]
  );

  // Function to add a command to history without executing it
  const addCommand = useCallback(
    (command: FieldCommand | ProductCommand) => {
      // Store the command in history without executing
      const newCommands = [...commands.slice(0, currentStateRef.current), command];
      setCommands(newCommands);
      commandsRef.current = newCommands;
      currentStateRef.current += 1;
      setCurrentState(currentStateRef.current);
    },
    [commands]
  );

  // Function to handle undo action
  const undo = () => {
    if (
      currentStateRef.current <= commandsRef.current.length &&
      currentStateRef.current > 0
    ) {
      currentStateRef.current -= 1;
      setCurrentState(currentStateRef.current);
      commandsRef.current[currentStateRef.current].undo();
    }
  };

  const reset = () => {
    setCurrentState(0);
    currentStateRef.current = 0;
    setCommands([]);
    commandsRef.current = [];
  };

  // Get the field path of the last changed field for UI feedback
  const getLastChangedField = useCallback(() => {
    if (currentStateRef.current > 0 && commandsRef.current.length > 0) {
      const lastCommand = commandsRef.current[currentStateRef.current - 1];
      if ("fieldPath" in lastCommand) {
        return lastCommand.fieldPath || null;
      } else if ("productName" in lastCommand) {
        return `product.${lastCommand.productName}`;
      }
    }
    return null;
  }, []);

  // Function to remove all commands for a specific product
  const removeCommandsForProduct = useCallback(
    (product: string) => {
      // Do NOT remove field-level commands for the product.
      // Only remove product-level commands (deletion/addition),
      // so that field-level history is preserved for undo/redo.
      const filteredCommands = commandsRef.current.filter((command) => {
        if ("productName" in command) {
          // Remove product-level commands for this product
          return command.productName !== product;
        }
        // Keep all field-level commands
        return true;
      });

      setCommands(filteredCommands);
      commandsRef.current = filteredCommands;
      // Adjust currentState if we removed commands before the current position
      const removedCount = commands.length - filteredCommands.length;
      if (removedCount > 0) {
        const newCurrentState = Math.max(0, currentStateRef.current - removedCount);
        currentStateRef.current = newCurrentState;
        setCurrentState(newCurrentState);
      }
    },
    [commands]
  );

  return useMemo(
    () => ({
      executeFieldCommand,
      executeProductCommand,
      addCommand,
      undo,
      reset,
      removeCommandsForProduct,
      canUndo: currentState > 0,
      getLastChangedField
    }),
    [
      executeFieldCommand,
      executeProductCommand,
      addCommand,
      currentState,
      commands.length,
      getLastChangedField,
      removeCommandsForProduct
    ]
  );
};

export default useArpsUndoManager;
